// File: frontend/src/components/scoring/FilmRoomSection.tsx
//
// Film Room section for the How to Play page and contests lobby: section
// header matching the Scoring / How to Play divider style, a two-tier selector
// (primary league tabs -> secondary player pills), and the shared chapter view. La Liga is the default
// league (owner call 2026-07-30).

import React, { useState } from 'react';
import { mixpanelTrack } from '@/services/mixpanel';
import {
  DEFAULT_LEAGUE_SLUG,
  chaptersForLeague,
  getChapter,
} from '@/data/filmRoomChapters';
import FilmRoomChapterView from './FilmRoomChapterView';
import FilmRoomSelector from './FilmRoomSelector';

interface FilmRoomSectionProps {
  surface?: string; // mixpanel Surface property, e.g. 'scoring-page' | 'contests-lobby'
}

const FilmRoomSection: React.FC<FilmRoomSectionProps> = ({ surface = 'scoring-page' }) => {
  const [leagueSlug, setLeagueSlug] = useState(DEFAULT_LEAGUE_SLUG);
  const [chapterSlug, setChapterSlug] = useState(chaptersForLeague(DEFAULT_LEAGUE_SLUG)[0]?.slug);
  const chapter = getChapter(chapterSlug);
  const handleLeagueSelect = (slug: string) => {
    if (slug === leagueSlug) return;
    setLeagueSlug(slug);
    const first = chaptersForLeague(slug)[0];
    if (first) setChapterSlug(first.slug);
    mixpanelTrack('Film Room League Selected', { League: slug, Surface: surface });
  };

  const handleChapterSelect = (slug: string) => {
    setChapterSlug(slug);
    mixpanelTrack('Film Room Chapter Selected', { Chapter: slug, Surface: surface });
  };

  return (
    <section>
      <div className="flex items-center gap-3 mb-4 md:mb-6 px-4">
        <div className="flex-1 h-px bg-gray-700/50" />
        <span className="text-lg md:text-2xl font-heading font-bold uppercase tracking-wider text-accent-yellow whitespace-nowrap">Film Room</span>
        <div className="flex-1 h-px bg-gray-700/50" />
      </div>
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-gray-400 text-center mb-4 px-4">
          Watch the points happen. Pick a player, click any scoring moment, and the official
          match video jumps to it.
        </p>
        <div className="mb-4 px-4">
          <FilmRoomSelector
            leagueSlug={leagueSlug}
            activeChapterSlug={chapter.slug}
            onLeagueSelect={handleLeagueSelect}
            onChapterSelect={handleChapterSelect}
          />
        </div>
        <FilmRoomChapterView
          chapter={chapter}
          compactPreview={surface === 'contests-lobby'}
          surface={surface}
        />
      </div>
    </section>
  );
};

export default FilmRoomSection;
